import { useEffect, useState } from 'react'
import { adminService } from 'core/services/adminService'
import { AdventItem } from './advent-item'
import { AdventEdit } from './advent-edit'
import { toast } from 'react-toastify'

import c from './assets/sass/advent-list.module.scss'

export const AdventList = () => {
    const [rewards, setRewards] = useState([])
    const [loading, setLoading] = useState(true)
    const [editing, setEditing] = useState(null)
    const [creating, setCreating] = useState(false)
    const [exporting, setExporting] = useState(false)
    const [duplicating, setDuplicating] = useState(false)
    const [deletingDay, setDeletingDay] = useState(null)
    const [fromDay, setFromDay] = useState(1)
    const [toDay, setToDay] = useState(2)
    const [collapsedDays, setCollapsedDays] = useState(new Set())

    useEffect(() => {
        loadRewards()
    }, [])

    const loadRewards = async () => {
        try {
            setLoading(true)
            const data = await adminService.getAdventRewards()
            setRewards(data.rewards)
        } catch (error) {
            console.error('Failed to load rewards:', error)
            toast('Ошибка загрузки наград', { type: 'error' })
        } finally {
            setLoading(false)
        }
    }

    const handleExport = async () => {
        try {
            setExporting(true)
            const data = await adminService.exportAdventClaims()
            const blob = new Blob([JSON.stringify(data.data, null, 2)], {
                type: 'application/json',
            })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `advent_claims_${new Date().toISOString().split('T')[0]}.json`
            a.click()
            URL.revokeObjectURL(url)
            toast('Экспорт выполнен', { type: 'success' })
        } catch (error) {
            toast('Ошибка экспорта: ' + error.message, { type: 'error' })
        } finally {
            setExporting(false)
        }
    }

    // Группируем награды по дням
    const rewardsByDay = rewards.reduce((acc, reward) => {
        if (!acc[reward.day]) {
            acc[reward.day] = []
        }
        acc[reward.day].push(reward)
        return acc
    }, {})

    if (loading) return <div className={c.loading}>Загрузка...</div>

    if (editing || creating) {
        return (
            <AdventEdit
                reward={editing}
                onSave={() => {
                    loadRewards()
                    setEditing(null)
                    setCreating(false)
                }}
                onCancel={() => {
                    setEditing(null)
                    setCreating(false)
                }}
            />
        )
    }

    const handleDuplicate = async () => {
        if (
            !window.confirm(
                `Скопировать награды с дня ${fromDay} на день ${toDay}?`
            )
        )
            return
        try {
            setDuplicating(true)
            const result = await adminService.duplicateAdventDay(fromDay, toDay)
            toast(result.message || 'Награды скопированы', { type: 'success' })
            await loadRewards()
        } catch (error) {
            toast('Ошибка: ' + (error.response?.data?.error || error.message), {
                type: 'error',
            })
        } finally {
            setDuplicating(false)
        }
    }

    const handleDeleteDay = async (day) => {
        const rewardsCount = rewardsByDay[day]?.length || 0
        if (
            !window.confirm(
                `Удалить день ${day} со всеми наградами (${rewardsCount} шт)?`
            )
        )
            return

        try {
            setDeletingDay(day)
            const result = await adminService.deleteAdventDay(day)
            toast(result.message || `День ${day} удален`, { type: 'success' })
            await loadRewards()
        } catch (error) {
            toast('Ошибка: ' + (error.response?.data?.error || error.message), {
                type: 'error',
            })
        } finally {
            setDeletingDay(null)
        }
    }

    const toggleDay = (day) => {
        setCollapsedDays((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(day)) {
                newSet.delete(day)
            } else {
                newSet.add(day)
            }
            return newSet
        })
    }

    return (
        <div className={c.component}>
            <div className={c.actions}>
                <button className={c.btn} onClick={() => setCreating(true)}>
                    + Добавить награду
                </button>
                <button
                    className={c.btnSecondary}
                    onClick={handleExport}
                    disabled={exporting}
                >
                    {exporting ? 'Экспорт...' : 'Экспорт БД'}
                </button>
            </div>

            <div
                className={c.info}
                style={{
                    padding: '12px',
                    background: 'rgba(255,211,89,0.1)',
                    borderRadius: '8px',
                    marginBottom: '16px',
                }}
            >
                <p
                    style={{
                        margin: 0,
                        fontSize: '13px',
                        color: 'rgba(255,255,255,0.7)',
                    }}
                >
                    💡 Для каждого дня можно добавить до 15 наград с разными
                    шансами выпадения.
                    <br />
                    При получении приза система выберет одну награду случайно по
                    шансам.
                </p>
            </div>

            <div
                className={c.duplicateSection}
                style={{
                    padding: '12px',
                    background: 'rgba(59,130,246,0.1)',
                    borderRadius: '8px',
                    marginBottom: '16px',
                }}
            >
                <h4
                    style={{
                        margin: '0 0 10px 0',
                        fontSize: '14px',
                        color: '#fff',
                    }}
                >
                    🔄 Дублирование дня
                </h4>
                <div
                    style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                    }}
                >
                    <label
                        style={{
                            fontSize: '13px',
                            color: 'rgba(255,255,255,0.8)',
                        }}
                    >
                        С дня:
                        <input
                            type="number"
                            min="1"
                            max="31"
                            value={fromDay || 1}
                            onChange={(e) =>
                                setFromDay(parseInt(e.target.value) || 1)
                            }
                            style={{
                                marginLeft: '8px',
                                width: '60px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: 'none',
                            }}
                        />
                    </label>
                    <label
                        style={{
                            fontSize: '13px',
                            color: 'rgba(255,255,255,0.8)',
                        }}
                    >
                        На день:
                        <input
                            type="number"
                            min="1"
                            max="31"
                            value={toDay || 2}
                            onChange={(e) =>
                                setToDay(parseInt(e.target.value) || 2)
                            }
                            style={{
                                marginLeft: '8px',
                                width: '60px',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: 'none',
                            }}
                        />
                    </label>
                    <button
                        className={c.btn}
                        onClick={handleDuplicate}
                        disabled={duplicating || fromDay === toDay}
                        style={{ padding: '6px 16px', fontSize: '13px' }}
                    >
                        {duplicating ? 'Копирование...' : 'Копировать'}
                    </button>
                </div>
                <p
                    style={{
                        margin: '8px 0 0 0',
                        fontSize: '11px',
                        color: 'rgba(255,255,255,0.5)',
                    }}
                >
                    ⚠️ Промокоды, которые уже были выданы в других днях, не
                    будут скопированы
                </p>
            </div>

            <div className={c.list}>
                {Object.keys(rewardsByDay)
                    .sort((a, b) => parseInt(a) - parseInt(b))
                    .map((day) => {
                        const isCollapsed = collapsedDays.has(day)
                        const isDeletingThisDay = deletingDay === day
                        return (
                            <div key={day} className={c.dayGroup}>
                                <div className={c.dayHeader}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            flex: 1,
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => toggleDay(day)}
                                    >
                                        <span className={c.collapseIcon}>
                                            {isCollapsed ? '▶' : '▼'}
                                        </span>
                                        <h3>День {day}</h3>
                                        <span
                                            style={{
                                                fontSize: '13px',
                                                color: 'rgba(255,255,255,0.5)',
                                            }}
                                        >
                                            {rewardsByDay[day].length}{' '}
                                            {rewardsByDay[day].length === 1
                                                ? 'награда'
                                                : 'наград'}
                                        </span>
                                    </div>
                                    <button
                                        className={c.deleteDayBtn}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleDeleteDay(day)
                                        }}
                                        disabled={isDeletingThisDay}
                                        title={`Удалить день ${day} со всеми наградами`}
                                    >
                                        {isDeletingThisDay ? '⏳' : '🗑️'}{' '}
                                        Удалить день
                                    </button>
                                </div>
                                {!isCollapsed &&
                                    rewardsByDay[day].map((reward) => (
                                        <AdventItem
                                            key={reward.id}
                                            reward={reward}
                                            onEdit={() => setEditing(reward)}
                                            onUpdate={loadRewards}
                                        />
                                    ))}
                            </div>
                        )
                    })}
            </div>
        </div>
    )
}
